import { getEnv } from '@/utils/helpers'

export const supportedLanguages = ['en', 'en-SA', 'en-US', 'fr', 'iv', 'ar', 'ar-SA', 'es', 'it', 'pt'] as const
export type SupportedLanguage = (typeof supportedLanguages)[number]

export interface LanguagePolicy {
  allowedLanguages: readonly SupportedLanguage[]
  defaultLanguage?: SupportedLanguage
}

/** Match a catalog without falling back to a different language. */
export function matchLanguage(value?: string | null): SupportedLanguage | undefined {
  return supportedLanguages.find((language: SupportedLanguage) => language.toLowerCase() === value?.trim().toLowerCase())
}

/** Empty settings preserve the existing deployment's language behavior. */
export function createLanguagePolicy(allowed?: string, defaultValue?: string): LanguagePolicy {
  if (!allowed?.trim() && !defaultValue?.trim()) return { allowedLanguages: supportedLanguages }
  if (!allowed?.trim() || !defaultValue?.trim()) {
    throw new Error('ALLOWED_LANGUAGES and DEFAULT_LANGUAGE must be configured together')
  }
  const allowedLanguages: SupportedLanguage[] = allowed.split(',').map((value: string) => {
    const language: SupportedLanguage | undefined = matchLanguage(value)
    if (!language) throw new Error('ALLOWED_LANGUAGES contains an unsupported locale')
    return language
  })
  const defaultLanguage: SupportedLanguage | undefined = matchLanguage(defaultValue)
  if (!defaultLanguage || !allowedLanguages.includes(defaultLanguage)) {
    throw new Error('DEFAULT_LANGUAGE must be included in ALLOWED_LANGUAGES')
  }
  return { allowedLanguages: [...new Set(allowedLanguages)], defaultLanguage }
}

export const languagePolicy: LanguagePolicy = createLanguagePolicy(
  getEnv('VITE_ALLOWED_LANGUAGES'),
  getEnv('VITE_DEFAULT_LANGUAGE'),
)

/** Keep allowed preferences; regional deployments replace all other values with their default. */
export function resolveLanguage(value?: string | null, policy: LanguagePolicy = languagePolicy): SupportedLanguage {
  const exact: SupportedLanguage | undefined = matchLanguage(value)
  if (exact && policy.allowedLanguages.includes(exact)) return exact
  if (policy.defaultLanguage) return policy.defaultLanguage
  return matchLanguage(value?.split('-')[0]) ?? 'en'
}
